<template>
  <table class="order-table">
    <tbody>
      <tr class="table-head">
        <th
          v-for="(head, headIndex) in tableHead"
          :key="headIndex"
          :style="getHeadStyle(head)"
        >
          {{ head.label }}
        </th>
      </tr>
    </tbody>
    <tbody v-for="(order, orderIndex) in list" :key="orderIndex" class="order">
      <!-- 订单商品列表 -->
      <tr
        v-for="(orderItem, orderItemIndex) in order.orderItems"
        :key="orderItemIndex"
        class="box-tr"
        :class="{ checked: orderItem.checked }"
      >
        <template v-for="(head, headIndex) in tableHead">
          <td
            v-if="getRowspan(head, order, orderItemIndex)"
            :key="headIndex"
            :class="{ 'row-td': head.isRowspan }"
            :rowspan="getRowspan(head, order, orderItemIndex)"
          >
            <slot
              v-if="head.slot"
              :name="head.slot"
              :head="head"
              :row="orderItem"
              :index="orderItemIndex"
              :order="order"
            ></slot>
            <component
              :is="head.component"
              v-else-if="head.component"
              :info="getContentObj(head, orderItem, order)"
            />
            <div v-else :class="head.rowClass">
              {{ getContent(head, orderItem, order) || '-' }}
            </div>
          </td>
        </template>
        <td
          v-if="orderItem.disabledText"
          :colspan="colspan"
          class="mask-td flex row-center"
          :style="{ right: maskRight }"
        >
          <div>{{ orderItem.disabledText }}</div>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script>
// 组件
import ProductCell from './ProductCell.vue'
// 工具
import { getPropByPath, strToThousands } from '@/utils/utils'
import { cloneDeep, isFunction, isObject } from 'lodash'

function parsePrice(value, prefix = '￥') {
  return prefix + strToThousands(Number(value)?.toFixed(2))
}

/**
 * @description 通用的订单列表组件，双层结构，支持表头配置
 */
export default {
  components: { ProductCell },

  props: {
    tableHead: {
      type: Array,
      default: () => []
    },

    tableData: {
      type: Array,
      default: () => []
    },

    // 遮罩绝对定位的right值，一般用于限制遮罩宽度
    maskRight: {
      type: [Number, String],
      default: 0
    }
  },

  data() {
    return {
      list: []
    }
  },

  computed: {
    colspan() {
      return this.tableHead.length
    }
  },

  watch: {
    tableData: {
      handler(val) {
        this.list = cloneDeep(val || [])
      },
      deep: true,
      immediate: true
    }
  },

  methods: {
    parsePrice,

    getHeadStyle(head) {
      if (head.width) {
        return Object.assign({}, head.style, { width: head.width })
      }
      return head.style
    },

    /**
     * 价格处理
     */
    getContent(config, data, order) {
      let result = config.prop ? getPropByPath(data, config.prop) : data
      // 优先执行filter过滤函数
      if (isFunction(config.filter)) {
        result = config.filter(result, order)
      }

      if (config.isPrice) {
        result = parsePrice(result)
      }
      return result
    },

    getContentObj(config, data, order) {
      let result = config.prop ? getPropByPath(data, config.prop) : data
      // 优先执行filter过滤函数
      if (isFunction(config.filter)) {
        result = config.filter(result, order)
      }
      if (isObject(result)) return result
      return { result: result }
    },

    getRowspan(head, order, orderItemIndex) {
      if (head.isRowspan) {
        if (orderItemIndex === 0) {
          return order.orderItems?.length || 1
        } else {
          return 0
        }
      }
      return 1
    },

    handleRemarkChange() {
      this.$emit('update:tableData', cloneDeep(this.list))
    }
  }
}
</script>

<style lang="scss" scoped>
.order-table {
  width: 100%;
  border-spacing: 0;
  border-collapse: collapse;
  font-size: 14px;

  .table-head {
    th {
      height: 32px;
      line-height: 32px;
      background-color: $info-fifth-color;
      margin-bottom: 15px;
      text-align: center;
      @include text-bold;
    }
  }

  .order {
    border: 1px solid $info-fourth-color;

    .box-head {
      background: $info-sixth-color;
      height: 32px;
      line-height: 18px;
      padding: 0 15px;
      border-top: 1px solid $info-fourth-color;

      .box-head-item {
        margin-right: 60px;
      }

      .order-no {
        color: $third-text-color;

        .num {
          color: $text-color;
          vertical-align: top;
          display: inline-block;
        }
      }

      .shop {
        display: block;
        cursor: pointer;

        .shop-icon {
          width: 14px;
          height: 14px;
          vertical-align: top;
          display: inline-block;
          margin-top: 2px;
          margin-right: 5px;
        }

        &:hover {
          color: $primary-color;

          .shop-icon {
            background-position: -9px -458px;
          }
        }
      }

      .remove-button {
        width: 14px;
        height: 14px;
        margin-top: 3px;
        cursor: pointer;
      }
    }

    .goods-number {
      line-height: 20px;
      color: $third-text-color;
      text-align: center;
    }

    .goods-amount {
      line-height: 20px;
      color: $text-color;
      text-align: center;
    }

    .order-amount,
    .order-status,
    .order-operation {
      border-right: none;
      border-bottom: none;
      flex-shrink: 0;
      line-height: 20px;
    }

    .order-status {
      .status {
        color: $third-text-color;
      }
    }

    .footer-form {
      width: 500px;
      margin-bottom: -18px;
    }
  }

  .box-tr {
    position: relative;

    td {
      border-top: 1px solid $info-fourth-color;
      padding: 15px 0;
      text-align: center;
    }

    .row-td {
      border-left: 1px solid $info-fourth-color;
    }

    &.box-space {
      td {
        border-left: 1px solid $white-color;
        border-right: 1px solid $white-color;
        height: 14px;
        padding: 0;
      }
    }

    &.checked {
      background: $primary-fourth-color;

      ::v-deep .el-input-number {
        .el-input__inner {
          background: $primary-fourth-color;
        }
      }
    }

    .mask-td {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      z-index: 999;
      background: rgba(0, 0, 0, 0.5);
      color: $white-color;
      font-size: 16px;
    }
  }
}
</style>
